/**
 * Marca PharuERP — direção Strata (camadas / inventário).
 *
 * Direção aprovada do Design System (Bloco Brand, opção B "Strata"): o símbolo
 * é um bloco arredondado com barras horizontais empilhadas de largura variável
 * e opacidade decrescente — camadas representando estoque, BOM, planejamento.
 * Vibe de software de gestão clássico, mas atualizado.
 *
 * Três variantes:
 *   - symbol: o bloco com as camadas (favicon, sidebar colapsada, avatares)
 *   - wordmark: "Dash[ERP em accent]" (cabeçalho, telas de auth)
 *   - full: symbol + wordmark lado a lado (rodapés, splash screens)
 *
 * Cores vêm dos tokens canônicos (tokens.css): o bloco usa --surface-2 /
 * --border-strong (neutros que invertem entre claro/escuro) e as camadas usam
 * --accent (a cor de destaque escolhida pelo usuário em Configurações → Tema &
 * cores). Assim o logo acompanha tema e accent automaticamente.
 *
 * O símbolo estático equivalente (favicon/PWA) vive em public/pharuerp-symbol.svg
 * com cores concretas (grafite + cobre) — ver scripts/gerar-pwa-icons.js.
 */
import React from "react";

export function PharuERPSymbol({ size = 32, title = "PharuERP", ...rest }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 84 84"
      role="img"
      aria-label={title}
      fill="none"
      {...rest}
    >
      <title>{title}</title>
      <rect
        x="4"
        y="4"
        width="76"
        height="76"
        rx="18"
        fill="var(--surface-2)"
        stroke="var(--border-strong)"
      />
      <rect x="20" y="20" width="44" height="10" rx="2" fill="var(--accent)" />
      <rect x="20" y="34" width="32" height="10" rx="2" fill="var(--accent)" opacity="0.7" />
      <rect x="20" y="48" width="38" height="10" rx="2" fill="var(--accent)" opacity="0.45" />
      <rect x="20" y="62" width="24" height="6" rx="2" fill="var(--accent)" opacity="0.25" />
    </svg>
  );
}

export function PharuERPWordmark({ height = 28, title = "PharuERP", ...rest }) {
  const width = Math.round((height * 360) / 74);
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 360 74"
      role="img"
      aria-label={title}
      fill="none"
      {...rest}
    >
      <title>{title}</title>
      <text
        x="0"
        y="54"
        fontFamily="Montserrat, system-ui, sans-serif"
        fontWeight="600"
        fontSize="56"
        letterSpacing="-1.4"
        fill="var(--ink)"
      >
        Pharu
      </text>
      <text
        x="160"
        y="54"
        fontFamily="Montserrat, system-ui, sans-serif"
        fontWeight="600"
        fontSize="56"
        letterSpacing="-1.4"
        fill="var(--accent)"
      >
        ERP
      </text>
    </svg>
  );
}

export function PharuERPLogo({
  symbolSize = 28,
  wordmarkHeight = 22,
  gap = 10,
  ...rest
}) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: `${gap}px`,
      }}
      aria-label="PharuERP"
      {...rest}
    >
      <PharuERPSymbol size={symbolSize} aria-hidden="true" />
      <PharuERPWordmark height={wordmarkHeight} aria-hidden="true" />
    </span>
  );
}

export default PharuERPLogo;
